import * as path from 'node:path';
import * as childProcess from 'node:child_process';
import * as stringUtils from './stringUtils';
import * as packageJson from '../../package.json';
import * as unzip from 'unzip-stream';
import pRetry from 'p-retry';
import * as fs from 'node:fs';
import * as crypto from 'node:crypto';
import { promises as fsPromises, statSync, createReadStream } from 'node:fs';
import type { PathLike } from 'node:fs';

const DOWNLOAD_RETRY = 3;

export function getCliLocation() {
    let cliLocation;
    if (!require.main) { // we're in a REPL
        return process.cwd(); // fall back on the current working directory
    }
    if (require.main.filename.includes('/src') || require.main.filename.includes('\\src') || process.env.IS_UNIT_TEST) {
        cliLocation = path.resolve(__dirname, '../../');
    } else {
        cliLocation = path.resolve(__dirname, !global.meta?.IS_LOCAL ? '' : '../');
    }

    return cliLocation;
}

export const download = async (url: string) => {
    // Lazily required to avoid circular dep (httpRequest requires logger which requires the utils to get the runnerVersion)
    const httpRequest = await import('../commons/httpRequest');
    return pRetry(() => httpRequest.download(url), { retries: DOWNLOAD_RETRY, factor: 1 });
};

export const downloadAndSave = async (url: string, saveToLocation: PathLike) => {
    const res = await download(url);
    return fsPromises.writeFile(saveToLocation, res.body);
};

export function getSourcePath(location: string, fileName?: string) {
    if (stringUtils.isURL(location)) {
        return fileName || path.join(process.cwd(), location.replace(/^.*[\\/]/, ''));
    }

    return fileName || path.basename(location);
}

export const getSource = async (location: string, fileName: string) => {
    const destFile = getSourcePath(location, fileName);
    if (stringUtils.isURL(location)) {
        return downloadAndSave(location, destFile);
    }

    return fsPromises.copyFile(location, destFile);
};

export const getSourceAsBuffer = async (location: string) => {
    if (stringUtils.isURL(location)) {
        return download(location);
    }
    return fsPromises.readFile(location);
};

export const unzipFile = async (srcZipFile: string, destZipPath: string) => {
    return createReadStream(srcZipFile).pipe(unzip.Extract({ path: destZipPath }));
};

/**
 * Extracts a .tgz (gzipped tar) file using the system tar command.
 * This is used for macOS Cloudflare tunnel binaries which come as .tgz files (The binaries for other platforms are already extracted).
 * Note: Only works on macOS/Linux where the tar command is available.
 */
export const extractTgz = (srcTgzFile: string, destPath: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        const tarProcess = childProcess.spawn('tar', ['-xzf', srcTgzFile, '-C', destPath], {
            stdio: ['ignore', 'pipe', 'pipe'],
        });

        // Collect all errors so they will be rejected with the error message.
        let stderr = '';
        tarProcess.stderr?.on('data', (data) => {
            stderr += data.toString();
        });
        tarProcess.on('error', (err) => {
            reject(new Error(`Failed to extract tgz file ${srcTgzFile}: ${err.message}. ${stderr}`));
        });

        tarProcess.on('close', (code) => {
            if (code !== 0) {
                reject(new Error(`Failed to extract tgz file ${srcTgzFile}: tar exited with code ${code}. ${stderr}`));
            }
            resolve();
        });
    });
};

export const getLocalFileSizeInMB = (fileLocation: PathLike) => {
    const stats = statSync(fileLocation);
    const fileSizeInBytes = stats.size;
    return fileSizeInBytes / 1_000_000;
};

export function getRunnerVersion() {
    try {
        // Lazy require this, since the version is set after the bundle was built
        // eslint-disable-next-line import/no-dynamic-require
        const pack = require(`${__dirname}${!global.meta?.IS_LOCAL ? '' : '..'}/package.json`) as typeof import('../../package.json');
        return pack.version;
    } catch {
        return '';
    }
}

export function getEnginesVersion() {
    try {
        return packageJson.engines.node;
    } catch {
        return '';
    }
}

/**
 * @param src Source directory to copy its contents
 * @param dest Target directory to copy to
 * @param createParentDirs Whether to create parent directories
 */
export async function copyDir(src: string, dest: string, createParentDirs = true) {
    const dirents = await fsPromises.readdir(src, { withFileTypes: true });
    await fsPromises.mkdir(dest, { recursive: createParentDirs });
    for (const item of dirents) {
        const srcPath = path.join(src, item.name);
        const destPath = path.join(dest, item.name);
        if (item.isDirectory()) {
            await copyDir(srcPath, destPath);
        } else {
            await fsPromises.copyFile(srcPath, destPath);
        }
    }
}

export async function doesPathExist(_path: string) {
    try {
        await fsPromises.access(_path);
        return true;
    } catch (err) {
        if (err.code === 'ENOENT') {
            return false;
        }
        // no permissions to check
        throw err;
    }
}


// Verify checksum of a file after downloading it, it will delete the file if the checksum mismatches
export const verifyChecksum = async (filePath: string, expectedSha256: string, hashAlgorithm = 'sha256',digestEncoding: crypto.BinaryToTextEncoding = 'hex'): Promise<void> => {
    const fileBuffer = await fs.promises.readFile(filePath);
    const hash = crypto.createHash(hashAlgorithm).update(fileBuffer).digest(digestEncoding);
    if (hash !== expectedSha256) {
        await fs.promises.unlink(filePath);
        throw new Error(`Checksum mismatch for ${filePath}. Expected ${expectedSha256}, got ${hash}`);
    }
};
